import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { extractFrames } from "@/lib/frames";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Sparkles, Trash2, Play, Eye, ScanEye, Pin, PinOff } from "lucide-react";
import { VideoCompare } from "@/components/VideoCompare";
import { UpgradeLock } from "@/components/UpgradeLock";
import { useAuth } from "@/contexts/AuthContext";

const L = {
  en: { title: "Video AI Analysis", subtitle: "Upload a clip. We sample 8 key-frames in your browser and GPT 5.6 Terra actually watches them to return strengths, improvements and drills.",
    upload: "Upload video", desc: "Context (optional)", descPh: "e.g. I'm the #9 in red, watch my first touch", focus: "What should we focus on", position: "Your position",
    analyze: "Run vision analysis", analyzing: "Watching the clip…", extracting: "Sampling key-frames…", frames: "key-frames captured",
    history: "Your analyses", strengths: "Strengths", improvements: "Improvements", drills: "Recommended drills", score: "AI score", observations: "What the AI saw", noFrames: "Frames not ready", unsupported: "This video format is not supported by your browser — try MP4 (H.264) or WebM" },
  fr: { title: "Analyse Vidéo IA", subtitle: "Charge un clip. On capture 8 images clés dans ton navigateur et GPT 5.6 Terra les regarde vraiment pour sortir tes points forts, axes d'amélioration et exercices.",
    upload: "Charger vidéo", desc: "Contexte (optionnel)", descPh: "ex : je suis le n°9 en rouge, regarde ma première touche", focus: "Ce que l'on doit analyser", position: "Ton poste",
    analyze: "Lancer l'analyse vision", analyzing: "Visionnage du clip…", extracting: "Capture des images clés…", frames: "images clés capturées",
    history: "Tes analyses", strengths: "Points forts", improvements: "Axes d'amélioration", drills: "Exercices recommandés", score: "Score IA", observations: "Ce que l'IA a vu", noFrames: "Images non prêtes", unsupported: "Format vidéo non lu par ton navigateur — essaie MP4 (H.264) ou WebM" },
};

export default function Video() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const { can, billing } = useAuth();
  const canVideo = can("video"), canCompare = can("compare");
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [frames, setFrames] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [desc, setDesc] = useState("");
  const [focus, setFocus] = useState("");
  const [position, setPosition] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pinnedIds, setPinnedIds] = useState(() => { try { return JSON.parse(localStorage.getItem("pp_video_pins") || "[]"); } catch { return []; } });
  const videoRef = useRef();

  const togglePin = (id) => setPinnedIds((p) => {
    const next = p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id];
    localStorage.setItem("pp_video_pins", JSON.stringify(next));
    return next;
  });
  const pinned = pinnedIds.map((id) => items.find((v) => v.id === id)).filter(Boolean);

  const load = () => api.get("/video").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) return toast.error("Max 200MB");
    setFile(f); setFrames([]); setDuration(null);
    setVideoUrl(URL.createObjectURL(f));
  };

  const onLoaded = async () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
    setExtracting(true);
    try { setFrames(await extractFrames(v, 8)); }
    catch { toast.error("Frame extraction failed"); }
    finally { setExtracting(false); }
  };

  const run = async () => {
    if (!file) return;
    if (frames.length === 0 && !desc.trim()) return toast.error(t.noFrames);
    setLoading(true);
    try {
      await api.post("/video/analyze", {
        filename: file.name,
        duration_sec: duration ? Math.round(duration) : null,
        size_mb: +(file.size / (1024 * 1024)).toFixed(2),
        description: desc, focus, position, frames, lang,
      });
      toast.success("Analysis ready");
      setDesc(""); setFocus("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const del = async (id) => { await api.delete(`/video/${id}`); if (pinnedIds.includes(id)) togglePin(id); load(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="video-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">GPT 5.6 TERRA · VISION SCOUT</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="video-title">{t.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.subtitle}</p>

      {!canVideo && <div className="mt-6"><UpgradeLock feature="video" requiredPlan="Pro" /></div>}
      {canVideo && billing?.limits?.video != null && (
        <div className="mt-4 text-[10px] font-mono-tech uppercase text-slate-400" data-testid="video-quota">{billing.usage?.video || 0} / {billing.limits.video} {lang === "fr" ? "analyses ce mois" : "analyses this month"}</div>
      )}

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className={`rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-4 ${!canVideo ? "opacity-50 pointer-events-none" : ""}`}>
          <label className="block cursor-pointer">
            <input type="file" accept="video/*" onChange={onFile} className="hidden" data-testid="video-file-input" />
            <div className="aspect-video rounded-lg border-2 border-dashed border-[#1E2638] hover:border-[#00F076]/60 overflow-hidden flex items-center justify-center bg-black">
              {videoUrl ? (
                <video ref={videoRef} src={videoUrl} controls muted playsInline preload="auto" onLoadedData={onLoaded} onError={() => toast.error(t.unsupported)} className="w-full h-full" />
              ) : (
                <div className="text-center text-slate-500">
                  <Upload className="w-8 h-8 mx-auto mb-3" />
                  <div className="font-mono-tech text-xs tracking-wider uppercase">{t.upload}</div>
                </div>
              )}
            </div>
          </label>

          {(extracting || frames.length > 0) && (
            <div data-testid="video-frames-strip">
              <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                <ScanEye className="w-3 h-3 text-[#00F076]" />
                {extracting ? t.extracting : <span data-testid="video-frames-count">{frames.length} {t.frames}</span>}
              </div>
              <div className="grid grid-cols-8 gap-1">
                {frames.map((f, i) => <img key={i} src={f} alt="" className="w-full aspect-video object-cover rounded border border-[#1E2638]" />)}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.desc}</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t.descPh} data-testid="video-desc-input" rows={2} className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.focus}</Label>
              <Input value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="video-focus-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
            </div>
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.position}</Label>
              <Input value={position} onChange={(e) => setPosition(e.target.value)} data-testid="video-position-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
            </div>
          </div>
          <Button onClick={run} disabled={loading || extracting || !file || (frames.length === 0 && !desc.trim())} data-testid="video-analyze-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            <Sparkles className="w-4 h-4 mr-2" />{loading ? t.analyzing : t.analyze}
          </Button>
        </div>

        <div className="space-y-4">
          {canCompare ? <VideoCompare pinned={pinned} lang={lang} onUnpin={togglePin} /> : pinned.length > 0 && <UpgradeLock feature="compare" requiredPlan="Elite" compact />}
          <div className="font-display font-bold uppercase text-xl">{t.history}</div>
          {items.length === 0 && <div className="text-slate-500 text-sm">—</div>}
          {items.map((v) => {
            const a = v.analysis || {};
            const sc = a.score || {};
            return (
              <div key={v.id} className="rounded-xl border border-[#1E2638] bg-[#121721] p-5" data-testid={`video-item-${v.id}`}>
                <div className="flex gap-3">
                  {v.thumbnail && <img src={v.thumbnail} alt="" className="w-24 aspect-video object-cover rounded border border-[#1E2638] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-mono-tech tracking-wider text-emerald-400 uppercase">
                      <Play className="w-3 h-3 inline mr-1" />{v.filename} · {v.duration_sec ? `${v.duration_sec}s` : "—"}
                      {v.frame_count > 0 && <span className="text-slate-400"> · <Eye className="w-3 h-3 inline" /> {v.frame_count}</span>}
                    </div>
                    <div className="font-display font-bold text-lg mt-1">{a.summary || (v.description || "").slice(0, 80)}</div>
                  </div>
                  <div className="flex shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => canCompare ? togglePin(v.id) : togglePin(v.id)} title={pinnedIds.includes(v.id) ? t.unpin : t.pin} className={`${pinnedIds.includes(v.id) ? "text-[#00F076]" : "text-slate-400"} hover:text-[#00F076]`} data-testid={`video-pin-${v.id}`}>
                      {pinnedIds.includes(v.id) ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(v.id)} className="text-slate-400 hover:text-red-400" data-testid={`video-delete-${v.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {Object.keys(sc).length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {Object.entries(sc).map(([k, val]) => (
                      <div key={k} className="rounded-lg bg-[#090C10] border border-[#1E2638] p-2 text-center">
                        <div className="text-[10px] font-mono-tech uppercase text-slate-400">{k.replace("_", " ")}</div>
                        <div className="font-display font-black text-2xl text-[#00F076]">{val}</div>
                      </div>
                    ))}
                  </div>
                )}
                {a.observations?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-mono-tech uppercase tracking-wider text-blue-400 mb-1">{t.observations}</div>
                    <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">{a.observations.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
                <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
                  {a.strengths?.length > 0 && (
                    <div>
                      <div className="text-xs font-mono-tech uppercase tracking-wider text-[#00F076] mb-1">{t.strengths}</div>
                      <ul className="list-disc list-inside space-y-1 text-slate-300">{a.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {a.improvements?.length > 0 && (
                    <div>
                      <div className="text-xs font-mono-tech uppercase tracking-wider text-amber-400 mb-1">{t.improvements}</div>
                      <ul className="list-disc list-inside space-y-1 text-slate-300">{a.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                </div>
                {a.drills?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 mb-2">{t.drills}</div>
                    <div className="space-y-1">
                      {a.drills.map((d, i) => (
                        <div key={i} className="text-xs font-mono-tech text-slate-300">
                          <span className="text-white font-semibold">{d.name}</span>
                          {d.purpose && <span className="text-slate-500"> — {d.purpose}</span>}
                          {d.sets_reps && <span className="text-[#00F076]"> · {d.sets_reps}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
